<template>
  <div class="white_box">
    <div class="question_head">
      <user-image
        :image-src="question.author.profilePhotoUri ?? undefined"
        size="sm"
      />
      <h3>Q. {{ question.text }}</h3>
    </div>
    <div class="author">
      <i class="far fa-thumbs-up" />
      <span class="upvote_count">{{ question.upvoteCount ?? 0 }}</span>
      <span>
        {{ question.author.displayName }} - <span v-date="question.updateTime" />
      </span>
      <button
        v-if="canEditLocation"
        class="delete"
        @click="confirmDeleteQuestion(question)"
      >
        <i class="far fa-trash-alt" />
      </button>
    </div>
    <div
      v-if="question.totalAnswerCount > 0"
      class="answers"
    >
      <div
        v-for="(answer, index) in question.topAnswers"
        :key="index"
        class="answer"
      >
        <div class="answer_head">
          <user-image
            :image-src="answer.author?.profilePhotoUri ?? undefined"
            size="xs"
            class="mr-1"
          />
          <h4><strong>A.</strong> {{ answer.text }}</h4>
        </div>
        <div class="author">
          <i class="far fa-thumbs-up" />
          <span class="upvote_count">{{ answer.upvoteCount ?? 0 }}</span>
          <span>
            {{ answer.author?.displayName ?? 'You' }} - <span v-date="answer.updateTime" />
          </span>
          <button
            v-if="canEditLocation"
            class="delete"
            @click="confirmDeleteAnswer(question)"
          >
            <i class="far fa-trash-alt" />
          </button>
        </div>
      </div>
    </div>
    <div
      v-else-if="canEditLocation"
      class="add_answer"
    >
      <textarea
        v-model="answerText"
        :class="`answer_text ${answerText ? 'expand' : ''}`"
        placeholder="Start typing here to add an answer for this question"
      />
      <p class="info-text">
        Spam answers may be removed by Google
        <i
          class="fal fa-question-circle tooltipster"
          title="
            Google will remove any answers that it deems as spam, this can be answers that include links,
            emails or phone numbers. If you post an answer and it disappears from the platform within 24 hours
            it will likely be due to it being flagged as spam. Unfortunately Google does not inform us if it deems
            an answer as spam at the point of posting.
          "
        />
      </p>
      <div
        v-if="questionsValidationErrors"
        class="validation_errors"
      >
        <small
          v-for="(error, index) in questionsValidationErrors['answerText']"
          :key="index"
          class="validation"
        >
          {{ error }}
        </small>
      </div>
      <div
        v-if="unacceptedContent"
        class="validation_errors"
      >
        <small class="validation">
          Please note: Answers that contain urls, phone numbers or email addresses are often removed by google
        </small>
      </div>
      <primary-button
        v-if="answerText"
        :class="[
          'submit_answer',
          answersLoading ? 'disabled' : ''
        ]"
        size="md"
        @click="createAnswer({selectedLocation, question, answerText})"
      >
        {{ answersLoading ? 'Submitting' : 'Submit' }} Answer
        <i
          v-if="answersLoading"
          class="fa fa-spinner fa-spin"
        />
      </primary-button>
    </div>
  </div>
</template>

<script>
import PrimaryButton from "../../../global/buttons/PrimaryButton";
import UserImage from "../../../global/assets/UserImage";
import {mapActions, mapGetters} from "vuex";
import {checkForUnacceptedContent} from "../../../../modules/gbp/questions";
import Swal from 'sweetalert2';

export default {
  components: {
    UserImage,
    PrimaryButton
  },
  props: {
    question: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      answerText: null,
    }
  },
  computed: {
    ...mapGetters('user', [
        'canEditLocation',
    ]),
    ...mapGetters('businessProfile', [
      'selectedLocation',
      'questionsValidationErrors',
      'answersLoading',
    ]),
    unacceptedContent() {
      return checkForUnacceptedContent(this.answerText);
    }
  },
  methods: {
    ...mapActions('businessProfile', [
      'clearQuestions',
      'fetchQuestions',
      'deleteQuestion',
      'createAnswer',
      'deleteAnswer',
    ]),
    confirmDeleteQuestion() {
      Swal.fire({
        title: 'Is this your question?',
        icon: 'warning',
        html: `We are only able to delete a question from Google if it was created by the account you used
          to connect this listing. Unfortunately, Google doesn't provide us with sufficient data to confirm if this
          is your question. If it's not, it will still be deleted from our platform, however it will appear again when
          we next refresh your listing's data (within 24 hours)`,
        reverseButtons: true,
        showCancelButton: true,
        confirmButtonText: 'It&apos;s my question. Please delete',
        confirmButtonColor: '#d33',
        showLoaderOnConfirm: true,
        backdrop: true,
        preConfirm: async () => {
          const {selectedLocation, question} = this;
          await this.deleteQuestion({selectedLocation, question})
              .then(response => {
                return response;
              })
              .catch(error => {
                Swal.showValidationMessage(
                    `Request failed: ${error}`
                )
              });
        },
        allowOutsideClick: () => !Swal.isLoading()
      }).then((result) => {
        if (result.isConfirmed) {
          Swal.fire({
            title: 'Deleted successfully',
            icon: 'success'
          });
          this.clearQuestions();
          this.fetchQuestions(this.selectedLocation);
        }
      });
    },
    confirmDeleteAnswer(answer) {
      Swal.fire({
        title: 'Is this your answer?',
        icon: 'warning',
        html: `We are only able delete an answer from Google if it was created by the account you used
          to connect this listing. Unfortunately, Google doesn't provide us with sufficient data to confirm if this is
          your answer. If not, it will still be deleted from our platform, however it will appear again when
          we next refresh your listing's data (within 24 hours)`,
        reverseButtons: true,
        showCancelButton: true,
        confirmButtonText: 'It&apos;s my answer. Please delete',
        confirmButtonColor: '#d33',
        showLoaderOnConfirm: true,
        backdrop: true,
        preConfirm: async () => {
          const {selectedLocation, question} = this;
          await this.deleteAnswer({selectedLocation, question, answer})
              .then(response => {
                return response;
              })
              .catch(error => {
                Swal.showValidationMessage(
                    `Request failed: ${error}`
                )
              });
        },
        allowOutsideClick: () => !Swal.isLoading()
      }).then((result) => {
        if (result.isConfirmed) {
          Swal.fire({
            title: 'Deleted successfully',
            icon: 'success'
          });
          this.clearQuestions();
          this.fetchQuestions(this.selectedLocation);
        }
      });
    }
  },
}
</script>

<style lang="scss" scoped>
.white_box {
  margin-bottom: 1rem;

  > .question_head {
    display: flex;
    align-items: center;
    margin-bottom: .5rem;

    > h3 {
      font-weight: 700;
      line-height: 1;
      letter-spacing: -0.042em;
      color: #040707;
      font-size: 1.5rem;
      margin-left: .5rem;
      margin-bottom: 0;
    }
  }

  > .answers {
    margin-top: 1rem;
    margin-left: .75rem;

    > .answer {
      &:not(:last-of-type) {
        margin-bottom: .5rem;
      }

      .answer_head {
        display: flex;
        align-items: center;
        margin-bottom: .5rem;

        > h4 {
          flex: 1;
          font-weight: 400;
          line-height: 1;
          letter-spacing: -0.042em;
          color: #040707;
          font-size: 1.25rem;
          margin-bottom: 0;
        }
      }
    }
  }

  > .add_answer {
    margin-top: .5rem;

    .validation_errors {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;

      .validation {
        text-align: left;
        color: red;
        margin-left: 10px;
      }
    }

    > .submit_answer {
      margin-top: .5rem;
      margin-left: auto;
      width: 205px;
    }
  }
}

.author {
  display: flex;
  font-weight: 500;
  line-height: 1;
  letter-spacing: -0.042em;
  color: #040707;
  font-size: .75rem;

  > i, > .upvote_count {
    margin-right: .25rem;
    color: #37B2A8;
  }

  > .delete {
    color: #F34F4F;

    &:hover {
      color: #bd3b3b;
    }
  }
}

.info-text {
  font-size: 11px;
  margin: 0;
}
</style>
